/**
 * Field migration on chart-type change.
 *
 * ## Problem
 *
 * `ChartFieldConfig` is a flat union-style interface with three distinct models:
 *
 * | Model      | Keys                                      | Chart types                                        |
 * |------------|-------------------------------------------|----------------------------------------------------|
 * | **X/Y**    | `x`, `y[]`, `series`, `yDimension`, `size`| bar, line, area, pie, scatter, heatmap, histogram, funnel |
 * | **KPI**    | `value`                                   | kpi                                                |
 * | **Table**  | `columns[]`, `sorting[]`                  | table                                              |
 *
 * When switching between chart types that share the same model (e.g. bar→line),
 * the existing fields carry over naturally. But cross-model switches
 * (e.g. bar→kpi or bar→table) leave orphan keys and the target model's keys
 * unset — so the UI appears empty.
 *
 * ## Solution
 *
 * This module provides a **pure function** `migrateFieldsForChartType` that:
 *
 * 1. Detects which model the source and target chart types belong to.
 * 2. If they share the same model, returns the fields unchanged (intra-model).
 * 3. If they differ, converts fields using sensible defaults:
 *
 * ### Cross-model migration rules
 *
 * | From → To        | Conversion                                                                        |
 * |------------------|-----------------------------------------------------------------------------------|
 * | **X/Y → KPI**    | First `y[0]` → `value`. Drop `x`, `y`, `series`, `yDimension`, `size`.           |
 * | **X/Y → Table**  | `x` → first column (agg=none), each `y[]` → column (keep agg). Drop rest.        |
 * | **KPI → X/Y**    | `value` → `y[0]`. Auto-pick first dimension from `viewColumns` as `x`. Drop `value`.|
 * | **KPI → Table**  | `value` → single column entry. Drop `value`.                                      |
 * | **Table → X/Y**  | First non-agg column → `x`, first agg column → `y[0]`. Drop `columns`, `sorting`.|
 * | **Table → KPI**  | First aggregated column → `value`. Drop `columns`, `sorting`.                     |
 *
 * When no meaningful mapping exists (e.g. KPI with no `value` set), the target
 * fields are returned empty ({}), letting the UI show its "pick a field" state.
 *
 * ## Usage
 *
 * Called from `handleChartTypeChange` in `VisualEditor.tsx`:
 *
 * ```ts
 * import { migrateFieldsForChartType } from '../fieldMapping/migrateFields';
 *
 * const migratedFields = migrateFieldsForChartType(
 *   visualConfig.fields ?? {},
 *   visualConfig.chartType,  // from
 *   newType,                 // to
 *   viewColumns,             // for auto-picking x dimension
 * );
 * ```
 *
 * @module migrateFields
 */

import type { DatasetViewColumn } from '../../../api/types';
import type {
  ChartFieldConfig,
  ChartTypeOption,
  MeasureField,
  TableColumn,
} from '../visualConfig';
import { getDefaultAggForColumn } from '../visualConfig/fieldConfig';

// ── Model classification ─────────────────────────────────────────────────────

type FieldModel = 'xy' | 'kpi' | 'table';

const KPI_TYPES: ReadonlySet<string> = new Set(['kpi']);
const TABLE_TYPES: ReadonlySet<string> = new Set(['table']);

/** Determine which field model a chart type belongs to. */
export function classifyModel(chartType: ChartTypeOption): FieldModel {
  if (KPI_TYPES.has(chartType)) return 'kpi';
  if (TABLE_TYPES.has(chartType)) return 'table';
  return 'xy';
}

// ── XY field capabilities per chart type ─────────────────────────────────────
//
// Defines which optional fields each XY-model chart type supports.
// Used for intra-XY migration: unsupported fields are stripped when
// switching between XY chart types (e.g. scatter→heatmap drops `size`).

interface XyFieldCaps {
  series: boolean;
  size: boolean;
  yDimension: boolean;
  multiMeasure: boolean;
}

const XY_FIELD_CAPS: Record<string, XyFieldCaps> = {
  bar:       { series: true,  size: false, yDimension: false, multiMeasure: true  },
  line:      { series: true,  size: false, yDimension: false, multiMeasure: true  },
  area:      { series: true,  size: false, yDimension: false, multiMeasure: true  },
  scatter:   { series: true,  size: true,  yDimension: false, multiMeasure: false },
  heatmap:   { series: false, size: false, yDimension: true,  multiMeasure: false },
  pie:       { series: false, size: false, yDimension: false, multiMeasure: false },
  funnel:    { series: false, size: false, yDimension: false, multiMeasure: false },
  histogram: { series: true,  size: false, yDimension: false, multiMeasure: false },
};

const DEFAULT_XY_CAPS: XyFieldCaps = {
  series: true, size: false, yDimension: false, multiMeasure: true,
};

/**
 * Strip fields not supported by the target XY chart type and
 * truncate y[] when the target doesn't support multi-measure.
 */
function cleanFieldsForXyType(
  fields: ChartFieldConfig,
  toType: ChartTypeOption,
): ChartFieldConfig {
  const caps = XY_FIELD_CAPS[toType] ?? DEFAULT_XY_CAPS;
  const result = { ...fields };

  if (!caps.series)      delete result.series;
  if (!caps.size)        delete result.size;
  if (!caps.yDimension)  delete result.yDimension;

  // Truncate y[] to single measure when target doesn't support multi
  if (!caps.multiMeasure && result.y && result.y.length > 1) {
    result.y = [result.y[0]];
  }

  return result;
}

// ── Key-stripping helpers ────────────────────────────────────────────────────

/** Remove all X/Y model keys, returning only non-XY keys. */
function stripXyKeys(fields: ChartFieldConfig): ChartFieldConfig {
  const { x: _, y: _y, series: _s, yDimension: _yd, size: _sz, ...rest } = fields;
  return rest;
}

/** Remove KPI model keys. */
function stripKpiKeys(fields: ChartFieldConfig): ChartFieldConfig {
  const { value: _, ...rest } = fields;
  return rest;
}

/** Remove Table model keys. */
function stripTableKeys(fields: ChartFieldConfig): ChartFieldConfig {
  const { columns: _, sorting: _s, ...rest } = fields;
  return rest;
}

// ── Migration logic ──────────────────────────────────────────────────────────

/**
 * Migrate chart field configuration when switching chart types.
 *
 * This is a **pure function** — it returns a new object without mutating the
 * original `fields`.
 *
 * @param fields         Current field configuration.
 * @param fromType       Chart type being switched *from*.
 * @param toType         Chart type being switched *to*.
 * @param viewColumns    Available columns from the dataset view (used for
 *                       auto-picking a dimension when migrating to X/Y).
 * @returns              Migrated field configuration for the target chart type.
 */
export function migrateFieldsForChartType(
  fields: ChartFieldConfig,
  fromType: ChartTypeOption,
  toType: ChartTypeOption,
  viewColumns: DatasetViewColumn[],
): ChartFieldConfig {
  const fromModel = classifyModel(fromType);
  const toModel = classifyModel(toType);

  // Same model — intra-model cleanup for XY types (strip unsupported fields).
  if (fromModel === toModel) {
    if (fromModel === 'xy') {
      return cleanFieldsForXyType(fields, toType);
    }
    return { ...fields };
  }

  // ── X/Y → KPI ──────────────────────────────────────────────────────────
  if (fromModel === 'xy' && toModel === 'kpi') {
    const firstY = fields.y?.[0];
    const cleaned = stripXyKeys(fields);
    if (firstY) {
      return { ...cleaned, value: { ...firstY } };
    }
    return cleaned;
  }

  // ── X/Y → Table ────────────────────────────────────────────────────────
  if (fromModel === 'xy' && toModel === 'table') {
    const columns: TableColumn[] = [];

    // x dimension → first table column (no aggregation)
    if (fields.x?.column) {
      columns.push({ column: fields.x.column, aggregation: 'none' });
    }

    // Each y measure → table column (preserve aggregation)
    for (const m of fields.y ?? []) {
      columns.push({ column: m.column, aggregation: m.aggregation ?? 'sum' });
    }

    const cleaned = stripXyKeys(fields);
    return {
      ...cleaned,
      columns: columns.length > 0 ? columns : undefined,
    };
  }

  // ── KPI → X/Y ──────────────────────────────────────────────────────────
  if (fromModel === 'kpi' && toModel === 'xy') {
    const cleaned = stripKpiKeys(fields);
    const result: ChartFieldConfig = { ...cleaned };

    // value → first y measure
    if (fields.value) {
      result.y = [{ ...fields.value }];
    }

    // Auto-pick first dimension column as x
    const dim = viewColumns.find(
      (c) => c.semantic_type === 'dimension' || c.semantic_type === 'time',
    );
    if (dim) {
      result.x = { column: dim.name };
    }

    return result;
  }

  // ── KPI → Table ────────────────────────────────────────────────────────
  if (fromModel === 'kpi' && toModel === 'table') {
    const cleaned = stripKpiKeys(fields);
    if (fields.value) {
      return {
        ...cleaned,
        columns: [{ column: fields.value.column, aggregation: fields.value.aggregation ?? 'sum' }],
      };
    }
    return cleaned;
  }

  // ── Table → X/Y ────────────────────────────────────────────────────────
  if (fromModel === 'table' && toModel === 'xy') {
    const cleaned = stripTableKeys(fields);
    const result: ChartFieldConfig = { ...cleaned };

    const cols = fields.columns ?? [];

    // First non-aggregated (or agg=none) column → x
    const dimCol = cols.find((c) => !c.aggregation || c.aggregation === 'none');
    if (dimCol) {
      result.x = { column: dimCol.column };
    }

    // Aggregated columns → y measures
    const measureCols = cols.filter((c) => c.aggregation && c.aggregation !== 'none');
    if (measureCols.length > 0) {
      result.y = measureCols.map((c) => ({
        column: c.column,
        aggregation: c.aggregation,
      } as MeasureField));
    } else if (cols.length > 0) {
      // Fallback: if all columns had agg=none, try to find a numeric from view
      const numCol = viewColumns.find((vc) => vc.semantic_type === 'measure');
      if (numCol) {
        result.y = [{ column: numCol.name, aggregation: getDefaultAggForColumn(numCol) }];
      }
    }

    return result;
  }

  // ── Table → KPI ────────────────────────────────────────────────────────
  if (fromModel === 'table' && toModel === 'kpi') {
    const cleaned = stripTableKeys(fields);
    const cols = fields.columns ?? [];

    // First aggregated column → value
    const aggCol = cols.find((c) => c.aggregation && c.aggregation !== 'none');
    if (aggCol) {
      return {
        ...cleaned,
        value: { column: aggCol.column, aggregation: aggCol.aggregation },
      };
    }

    // Fallback: first column with default aggregation
    if (cols.length > 0) {
      const vc = viewColumns.find((v) => v.name === cols[0].column);
      const agg = vc ? getDefaultAggForColumn(vc) : 'sum';
      return {
        ...cleaned,
        value: { column: cols[0].column, aggregation: agg },
      };
    }

    return cleaned;
  }

  // Fallback (should never reach here)
  return { ...fields };
}